/** @module csi/slicer/slicerCalc */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"mathjs",
	"csi/_base/underscore",
	"csi/enums",
	"csi/locale",
	"csi/reflection",
	"csi/store/ExtractValues",
	"csi/types",
	"csi/xml"
], function (declare, Memory, { evaluate }, _, enums, locale, reflection, ExtractValues, types, xml) {
	/**
	 * This module mainly operates with three big objects: cube, columns and rows.
	 *
	 * First object is CUBE object. It is our source data, contains all required source data for pivot table.
	 * We receive it from the server in function slicerWidget._onPreViewSearch();
	 *
	 * Object format is two dimensional array, which forms the cube. Before we send request, we create a list of required classifiers and metrics.
	 * Those classifiers and metrics become cube columns and each cell of the cube contains one value for requested classifier or metrics.
	 * Example:
	 * if we request something like: Classifier 1, Classifier 2, Metrics 1, Metrics 2
	 * then we will receive the Cube object in the form of {string|number[][]}:
	 * [
	 *  [Value 1, Value2, Value3, Value 4],
	 *  [Value 5, Value6, Value7, Value 8],
	 *  [Value 9, Value10, Value11, Value 12]
	 * ]
	 *
	 * When we parse the cube (function slicerCalc.parseCube), we form two objects, which objects fully describe:
	 *   1) columns structure (colTree) - aka Columns Tree
	 *   2) rows and data (treeData) - aka Tree Data
	 *
	 * First object we call colTree (Columns Tree). Object being declared and created in function slicerCalc.parseCube()
	 * Object contains information about columns. First of all, this is information about column id (field),
	 * which uniquely identify column, links column to the data in treeData and also links column to dgrid.
	 * Object also provides information about metric name and column key.
	 *
	 * Higher level columns don't have columnId, as they only used to form Compound Columns, so they only have column name key;
	 * Bottom level column have column id (field) and metric name (metric).
	 *
	 * The nature of colTree is nested object aka "hashObject" as we frequently use it like hash table.
	 *		{
	 *			column1:{			// columnN = ClassifierName|MetricName - not a real "name" but a "key"
	 *				subColumn1:{	// subColumnM = ClassifierName|MetricName  - not a real "name" but a "key"
	 *					field: 0,
	 *					metric: targetRavenu
	 * 				}
	 *				subColumn2:{
	 *					field: 1,
	 *					metric: targetRavenu
	 * 				}
	 * 				...
	 *				subColumnY:{
	 *					field: N,
	 *					metric: targetRavenu
	 * 				}
	 * 			... ,
	 *			columnX:{
	 *				subColumnN:{
	 *					field: {number},
	 *					metric: {string}
	 * 				}
	 * 				...
	 *				subColumnM:{
	 *					field: {number},
	 *					metric: {string}
	 * 				}
	 * .....
	 *
	 * "field" is always number, except of CompareColumns, where it has a form of {number}+{string}/(number+suffix)
	 * "metric" is always {string}
	 *
	 * The order of nested columns does matter and define actual order of columns in slicer.
	 * We can manipulate those columns to achieve desired column order. For instance, if you swap columns
	 * position in colTree, they will swap their position in slicer
	 *
	 * Third important object is treeData, which holds actual data and link it to columns in colTree and dgrid.
	 *
	 * Each level of nested treeData object may have Number and Object fields:
	 * { field:value, field:value, field:value, field:value, RowName:{  // root or top most level. grand totals
	 * 		field:value, field:value, RowName: {						// sub totals
	 * 				field:value, field:value, field:value				// cell values
	 * 		}
	 * 	 }
	 * }
	 * legend:
	 * Field is columnId and this link this cell to colTree and to dgrid;
	 * Value is {number} metrics value;
	 * RowName - key to the name of nested row;
	 *
	 * What are these values for? Where are grid cell values, where are grand totals, where are sub totals?
	 * Top (root) level object's field:values are grand totals
	 * Bottom levels - slicer cell values
	 * levels between top and bottom - are sub totals
	 *
	 * Each row starts with RowName which is classifier key
	 * Rows and Subrows have the same structure, bottom level rows are slicer data and have no nested objects, just field:value pairs.
	 *
	 * Manipulation examples:
	 *
	 * Example 1: Swap columns. To swap two columns we use colTree object and swap columns objects location inside parent object. Good example for this
	 * is function this.sortColumns(): this function resolve real name of columns (key->name), then sort those real names and finally recreate object
	 * that holds columns, putting columns in desired order
	 *
	 * Example 2: Insert New Column. If we would want just create a column, make it visible but leave empty, then we would manipulate with only colTree object.
	 * All we need to do is to create column object
	 * columnKey:{
	 *		field: {number},
	 *		metric: {string}
	 * }
	 * and then insert it in the desired level and position inside colTree object. We use this when we insert compare columns (function this.addTotalColumns())
	 * and insert total columns (function this.addCompareColumns()). Both these functions analyze existing structure by traversing colTree object first, and
	 * then create column object and insert it.
	 *
	 * Usually we don't need just empty columns, we have to populate them with some data. Good examples where we do this for TotalColumns is
	 * function this.parseTotalColumnsStatic()) and for Compare Columns function this.parseCompare(). In order to populate columns, we have
	 * to manipulate with treeData object. Let's say we want to add value 14 for new column with id=57. Then, to add value to SubRows24, we
	 * will need to add field:value (57:14) pair inside object SubRows24. Position of field:value inside SubRows24 does not matter.
	 *
	 * Typical scenario: first add new column to colTree, and then populate added column with values (in ideal case we populate every
	 * row with new field, but sometimes, some cells might be left undefined). To add values we iterate through treeData object
	 * and add field:data to every (or to some) subRow objects.
	 *
	 * There are something about treeData that you will probably need to know: treeData object may and often does contains field:value members for
	 * "hidden columns", columns which does not have corresponding columns in colTree. We have to do this because of dynamic metrics.
	 * Some dynamic metrics may refer to other metrics, which were not selected by user to be visible. We will include those metrics
	 * to server request and will receive their value and place them inside treeData but will never add them to colTree, therefore we
	 * can use them for calculation but keep them hidden. We call those hidden metrics "virtual" or "hidden". This being done by adding
	 * names of virtual metrics to the metrics array - please refer to the function ParseCube variable "metrics". We use variable "countAdded" to
	 * track the number of added metrics. This is only for slicer calculation, extended list of metrics only exists inside this module during the calculations.
	 * After calculations done, the only tracks of virtual metrics - data inside treeData object.
	 *
	 * And one more important thing: when we add column, we always add columns for all metrics at once, assigning columnId to each visible and hidden column. And
	 * column ids must be CONSEQUENT and order of added columnIds must be the same when assigning new columnID to metric. Look at parseCube().metrics and if you
	 * need to add column, never add single column but rather add as many columns as many metrics we have.
	 *
	 * Slicer configuration basically consists of rowClassifiers, columnClassifiers and metrics. Column Classifiers and metrics
	 * define columns. Columns may be compound columns (used to create compound header) and real column (bottom level). Each real column always refer to one metric.
	 * Metrics can be real (visible, defined by user) and virtual (hidden, only for calculations). colTree only refer to visible metrics. treeData contain both
	 * visible and hidden data.
	 *
	 * Good example of that is function this.addTotalColumnsFields(). What this function does - for newly added total column it reserve field ids for every visible
	 * and virtual metric. If we don't have all metrics - how would we calculate dynamic metrics and totals?
	 * And those ids are consequent. This is also the must.
	 * There are number of places where we depend on it, for instance small local function getMetricIdx().
	 *
	 * So, what is over-all logic of slicer calculations, how the entire system works? Step by step:
	 * 1 First we parse the cube. Function this.parseCube(). This function parsing Cube data that we receive from the server. All other calculating functions will be
	 * called from inside of parseCube().
	 * 2 Once parsing is done, we do alphabetical column sorting, function this.sortColumns(). This function manipulate colTree to sort columns and treeData to sort rows.
	 * Data will not be messed up because it bounded to column via "field" value.
	 * 3 Now sort rows, function this.sortRows() manipulating treeData to sort and reorder rows alphabetically. Notice: at this point we don't have totals values
	 * inside treeData, only row objects and static metrics values on the very bottom level of structure.
	 * 4 Adding compareColumns: function this.addCompareColumns(). But we don't fill added column with data yet, we will call parseCompare() later, after
	 * dynamic metrics will be parsed
	 * 5 Add totalColumns: function this.addTotalColumns(). This function manipulate colTree to insert required total columns.
	 * 6 Populate static metrics values for total columns: function this.parseTotalColumnsStatic(). This function manipulates with treeData and adds values for
	 * total columns (not total rows yet). But only values for static metrics. Dynamic values for total columns will be calculated later, together with
	 * other dynamic metrics values.
	 * 7 Now it is a time to calculate totals and sub totals rows. Function this.parseTotals() manipulates with treeData and also service total rows of total columns.
	 * 8 Calculate Dynamic metrics. Function this.parseDynamics(). Taking care of dynamic values, and service totals columns as well.
	 * 9 Calculate totals for "%mix" custom metrics, which refer to dynamic metric. Function this.parseCustomTotals()
	 * 10 Calculate custom metrics. Function this.parseCustom(). Calculate custom metric and for now set "undefined" for totals. They will be calculated later.
	 * 11 Calculate totals for custom Metrics. Function this.parseDynamicRowTotals().
	 * 12 Now when all values are calculated it is time to take care of compare columns. Each compare column has a reference to both columns being compared, so
	 * all we need to to is to go to these columns and calculate difference. Function this.parseCompare().
	 * 13 We need to create a store for values of top most column classifier: function this.firstColClsValuesStore()
	 * 14 Create store for all columnClassifier names {id, name}: function this.colClsNamesStore()
	 * 15 Create Dummy Column, used for column resizing as a place holder for extra space on the right side of grid.
	 *
	 * Slicer might be empty: there are special case if there are no metrics or no row classifiers. Function parseCube will not fail, but return empty objects.
	 * In case of empty slicer we create empty stores for columnClassifier names and first classifier values.
	 */

	/** Custom metrics operations constants */
	var OP_SUM = "sum";
	var OP_DIFFERENCE = "dif";
	var OP_PRODUCT = "prd";
	var OP_AVERAGE = "avg";
	var OP_MIX = "mix";
	var OP_MARGIN = "mrg";
	var OP_MARKUP = "mrk";

	/** Dynamic metrics aggregations constants */
	var AGGREGATION_FUNCTION = "fnc";
	var AGGREGATION_SUM = "sum";
	var AGGREGATION_MINIMUM = "min";
	var AGGREGATION_MAXIMUM = "max";
	var AGGREGATION_AVERAGE = "avg";

	/** Static metrics aggregation functions */
	var FUNC_SUM = "SUM";
	var FUNC_MIN = "MIN";
	var FUNC_MAX = "MAX";
	var FUNC_AVG = "AVG";
	var FUNC_WEIGHT_AVG = "WEIGHT_AVG";
	var FUNC_COUNT = "COUNT";
	var FUNC_UNIQ = "UNIQ";

	/** Character to separate parts of path */
	var DELIMITER_CHAR = '\x001';
	var COMPARE_COL_SUF = 'c';
	var TOTAL_COL_SUF = 't';

	/** Constants **/
	var MIX_COL_SUF = "__mix__";
	var MIX_ROW_GRANDTOTAL = "rowgrand";
	var MIX_ROW_SUBTOTAL = "rowsub";
	var MIX_ROW_TOTALGRAND = "totalgrand";

	/** Variables for column compare calculations */
	var cmpVs = {};
	var cmpVsThis = {};

	function isVar(obj) {
		return (obj != null);
	}

	/**
	 * If we configured metrics A and B to be shown, and it also required metric C (added metric) then we will have three
	 * metrics to be repeated for each classifier. This function finds the offset where current set of metrics starts
	 * @param {string} metric - metric key
	 * @param {MetricObject[]} colMetrics - array of metrics per column
	 * @param {number} colIdx - index of the column for which we are finding matching metric column
	 * @param {number} totalMetricsNum - self._config.metrics.length + countAdded
	 * @return {number}
	 */
	function getMetricIdx(metric, colMetrics, colIdx, totalMetricsNum) {
		var idx = colIdx - colIdx % totalMetricsNum;
		while (colMetrics[idx].id != metric) { idx++; };
		return idx;
	}

	/**
	 * Return aggregation function for given dynamic metric or null, if no metric or static metric
	 * @param {MetricObject} metric
	 * @return {string} - AGGREGATION_FUNCTION, AGGREGATION_SUM, AGGREGATION_MINIMUM, AGGREGATION_MAXIMUM, AGGREGATION_AVERAGE or null
	 */
	function getDynamicAggrFunc(metric) {
		if (metric && metric.dynamic) {
			if (metric.custom) {
				return metric.presentation.total || AGGREGATION_FUNCTION; // assign default aggregation function if not specified
			} else {
				return metric.presentation || AGGREGATION_FUNCTION; // assign default aggregation function if not specified
			}
		}
		return null;
	}

	/**
	 * Check if provided column id is Total Column's id
	 * @param {sting} colId
	 * @return {boolean}
	 */
	function isTotalColId(colId) {
		return colId.indexOf(TOTAL_COL_SUF + DELIMITER_CHAR) >= 0;
	}

	/**
	 * Verify if specified column is total column
	 * @param {Number} colIdx
	 * @param {ColTree} colTree
	 * @return {boolean} - return true if specified colIdx belongs total column
	 */
	function isTotalColumn(colIdx, colTree) {
		return _.some(colTree, function (value, colName) {
			var field = value.field;
			if (_.isNumber(field)) {
				if ((field == colIdx) && (isTotalColId(colName))) {
					return true;
				} else {
					return false;
				}
			} else {
				return isTotalColumn(colIdx, value);
			}
		});
	}

	/**
	 * Sort fields by index (enums) or by label (all other)
	 * @param {Object[]} fields
	 * @return {Object[]}
	 */
	function sortFields(fields) {
		return _.sortBy(fields, function (obj) {
			var idx = obj.enumIdx;
			if (idx != null) {
				if (idx < 0) {
					idx = enums.getIndexForOldEnum(obj.value);
				}
				return idx; // Sort data by enum index
			} else {
				return obj.label;  // Sort data by label
			}
		});
	}

	return declare(null, {
		/** Character to separate parts of path */
		DELIMITER_CHAR: DELIMITER_CHAR,
		COMPARE_COL_SUF: COMPARE_COL_SUF,
		TOTAL_COL_SUF: TOTAL_COL_SUF,
		/** {string} - slicer job time */
		dataDate: "",
		/** {number[]} _totalCount */
		_totalCount: [],
		/** {Memory} _firstColClsValues - {id, name} of first column classifier values. Used for Compare Dialog */
		firstColClsValuesStore: null,
		/** {Memory} _colClsNames - {id, name} of column classifiers. Used for Compare Dialog */
		colClsNamesStore: null,
		/** {boolean} bWeightedAverageProcessing - if true, means we have both weight and weighted average metrics, wich require special processing */
		bWeightedAverageProcessing: false,
		/** {boolean} bEmpty - slicer is empty due to no metrics and/or no row classifiers selected */
		bEmpty: false,

		delimiter: function (str) {
			return (str ? this.DELIMITER_CHAR : '');
		},

		/**
		 * Row Totals calculations for one cell.
		 *
		 * This function deals with static metrics only. Dynamic metrics will be calculated later.
		 *
		 * For now, for dynamic metrics, we assign value 0, we have to do this as we need to define some number for each metric cell that have
		 * or will have particular value. Alternatively, there might be cells without value and we have to distinguish them. If we don't put 0 to
		 * each dynamic metric, further algorithms will treat this cell value as cell without value.
		 *
		 * Static metrics parsing has two parts: particular cell value calculation, and sub/grand totals calculations.
		 *
		 * Aggregation, aka cell value calculation: When we parse the cube, it is possible to have a situation, when some cells of slicer grid have
		 * several values from cube belonging to this cell. Each static metric has "aggregation function" (metric object's field "func"). This function defines
		 * how we calculate cell value if it correspond to several cube values.
		 *
		 * When we parse cube (function parseCube()) for each cell (each value in treeData) we create array of values instead of just single value.
		 * Here in this function we apply aggregation function to every cell array and calculate cell value. We also store data for sub/grand total
		 * calculations in res[] array.
		 *
		 * res[] is single dimension array and has format {{columnId}field:{number|object}value} and contains column id and the data, required for total row calculations.
		 * field is either number or number with suffix (for compare and total columns).
		 * value:
		 * For aggregation functions COUNT or SUM, value of res[] contains number, which is sum of all previous value
		 * For aggregation function MIN and MAX, value of res[] contains current minimum or maximum value of all values of current field parsed so far
		 * For aggregation function AVG, value of res[] contains object with current sum and number of elements {val, count}
		 * For aggregation function UNIQ, value of res[] contains array with all unique values of current field. We will count those unique value on final step.
		 *
		 * @param {number|number[]} value - cell value
		 * @param {number} colIdx - column id
		 * @param {Object[]} obj - source data for calculation, leaves or sub branches of the branch
		 * @param {Array} res - calculation results for the whole row
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 */
		calcTotalStepValue: function (value, colIdx, obj, res, colMetrics) {
			if (_.isNumber(value)) {
				value = [value];
			}
			if (_.isArray(value) || value.count) {
				var metric = colMetrics[colIdx];
				if (metric.dynamic && !metric.total && !metric.uniq) {
					obj[colIdx] = 0;
					res[colIdx] = 0;
				} else {
					var theVal;
					var func = metric.func;
					if (metric.total) {
						func = FUNC_SUM;
					} else if (metric.uniq) {
						func = FUNC_UNIQ;
					}
					switch (func) {
						case FUNC_COUNT:
						case FUNC_SUM:
							theVal = 0;
							_.each(value, function (val) { // client side aggregation
								theVal += val;
							});
							res[colIdx] = (res[colIdx] || 0) + theVal;
							obj[colIdx] = theVal;
							break;
						case FUNC_MIN:
							theVal = value[0];
							_.each(value, function (val) { // client side aggregation
								theVal = Math.min(theVal, val);
							});
							res[colIdx] = Math.min((res[colIdx] || theVal), theVal);
							obj[colIdx] = theVal;
							break;
						case FUNC_MAX:
							theVal = value[0];
							_.each(value, function (val) { // client side aggregation
								theVal = Math.max(theVal, val);
							});
							res[colIdx] = Math.max((res[colIdx] || theVal), theVal);
							obj[colIdx] = theVal;
							break;
						case FUNC_AVG:
							theVal = { val: 0, count: 0 };
							if (!res[colIdx]) {
								res[colIdx] = { val: 0, count: 0 };
							}
							if (value.count) { // calculating subtotals
								theVal.val += value.val;
								theVal.count += value.count;
							} else { // calculating metric value
								_.each(value, function (val) { // client side aggregation
									theVal.val += val;
									theVal.count++;
								});
							}
							res[colIdx].val += theVal.val;
							res[colIdx].count += theVal.count;
							if (value.value) {
								obj[colIdx] = value.value;
							} else {
								obj[colIdx] = theVal.val / theVal.count;
							}
							break;
						case FUNC_WEIGHT_AVG:
							theVal = { val: 0, count: 0 };

							const weight = obj[getMetricIdx(colMetrics[metric.weightAttributeIdx].id, colMetrics, colIdx, this.totalMetricsNum)]

							if (!res[colIdx]) {
								res[colIdx] = { val: 0, count: 0 };
							}
							if (value.count) {
								// calculating subtotals
								theVal.val += value.val;
								theVal.count += weight;
							} else {
								// calculating metric value
								_.each(value, function (val, idx) {
									// client side aggregation
									theVal.val += weight ? val * weight[idx] : 0;
									theVal.count += weight ? weight[idx] : 0;
								});
							}
							res[colIdx].val += theVal.val;
							res[colIdx].count += theVal.count;
							if (value.value) {
								obj[colIdx] = value.value;
							} else {
								obj[colIdx] = theVal.val / theVal.count;
							}
							break;
						case FUNC_UNIQ:
							if (!res[colIdx]) {
								res[colIdx] = [];
							}
							_.each(value, function (val) { // client side aggregation
								if (res[colIdx].indexOf(val) < 0) {
									res[colIdx].push(val);
								}
							});
							obj[colIdx] = value.length;
							break;
						default:
							console.error('calcTotalStepValue: unknown metric func');
					}
				}
			}
		},

		/**
		 * Perform Row Totals calculations for single row
		 * @param {TreeData} treeData - bottom level of TreeData, see module description for details.
		 * @param {Array} res - calculation results for the whole row. See calcTotalStepValue() for detailed description.
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 */
		calcTotalStep: function (treeData, res, colMetrics) {
			if (this.bWeightedAverageProcessing) {
				// first process weighted average as it depend on weight, and weight can't be integrated before average calculation
				_.each(treeData, function (value, colIdx, obj) {
					const metric = colMetrics[colIdx];
					if (metric && metric.weightAttributeName) {
						this.calcTotalStepValue(value, colIdx, obj, res, colMetrics);
					}
				}, this);
				// now when we taken care of weighted average process all the rest but not weighted average which is already processed
				_.each(treeData, function (value, colIdx, obj) {
					const metric = colMetrics[colIdx];
					if (!metric || !metric.weightAttributeName) {
						this.calcTotalStepValue(value, colIdx, obj, res, colMetrics);
					}
				}, this);
			} else {
				_.each(treeData, function (value, colIdx, obj) {
					this.calcTotalStepValue(value, colIdx, obj, res, colMetrics);
				}, this);
			}
		},

		/**
		 * Perform final step of Row Totals calculations, after all rows of current branch was processed
		 * @param {TreeData} treeData - bottom level of TreeData, see module description for details.
		 * @param {Array} res - calculation results for the whole row. See calcTotalStepValue() for detailed description.
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 */
		calcTotalFinale: function (treeData, res, colMetrics, bLast) {
			_.each(res, function (value, colIdx) {
				if (value != undefined) {
					var metric = colMetrics[colIdx];
					if (!metric.dynamic || metric.total || metric.uniq) {
						var func = metric.func;
						if (metric.total) {
							func = FUNC_SUM;
						} else if (metric.uniq) {
							func = FUNC_UNIQ;
						}
						switch (func) {
							case FUNC_SUM:
							case FUNC_COUNT:
							case FUNC_MIN:
							case FUNC_MAX:
								break;
							case FUNC_AVG:
							case FUNC_WEIGHT_AVG:
								value.value = value.val / value.count; // final calculation of number of average
								break;
							case FUNC_UNIQ:
								break;
							default:
								console.error('calcTotalFinale: unknown metric func');
						}
					}
					// Assign sub/grand total value from res to treeData
					if (bLast && (value.value != undefined)) {
						treeData[colIdx] = value.value; // convert object, used for average calculations to number value
					} else {
						treeData[colIdx] = value;
					}
					if (metric.total && bLast) {
						this._totalCount[colIdx] = value; // save TOTAL(__Count__) value for dynamic metrics calculations
					} else if (metric.uniq && bLast) {
						treeData[colIdx] = value.length; // convert Count Unique object data to number value - we need number of unique values
					}
				}
			}, this);
		},

		/**
		 * Calculate totals for current depth. Metrics to be taken from next level, totals to be put on current level of treeData
		 * @param {TreeData} treeData - described in module description.
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {boolean} bLast - last row in tree time to calculate totals
		 */
		calcTotals: function (treeData, colMetrics, bLast) {
			var res = [];
			_.each(treeData, function (row) {
				this.calcTotalStep(row, res, colMetrics);
			}, this);
			this.calcTotalFinale(treeData, res, colMetrics, bLast);
		},

		/**
		 * Parse through colData. Recurring function
		 * We call this function several times, each time with different level. First we call it from top most
		 * level and calculate grand total, then we call t from level 1 and calculate sub totals of level 1 etc..
		 *
		 * When we go as deep as 1 level before bottom - we calculate data and return.
		 *
		 * @param {TreeData} treeData - described in module description.
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {number} treeDepth - depth of tree,how many levels down are metrics.
		 * @param {number} lvl - current level of parsing
		 * @param {bolean} blast - parsing root level of colData (last call)
		 */
		parseTotals: function (treeData, colMetrics, treeDepth, lvl, bLast) {
			if (lvl == treeDepth - 1) { // we now can reach metrics
				this.calcTotals(treeData, colMetrics, bLast);
			} else {
				_.each(treeData, function (row) {
					this.parseTotals(row, colMetrics, treeDepth, lvl + 1, bLast);
				}, this);
			}
		},

		/**
		 * Calculate value of dynamic metric
		 * @param {string} expr - expression to be calculated
		 * @param {array} data - line of cube data
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {number} colIdx - index of current column
		 * @param {number} countAdded - number of added metrics (for dynamic calculations)
		 * @return {number}
		 */
		calcMetric: function (expr, data, colMetrics, colIdx, countAdded) {
			var widget = this._slicerWidget;
			const scope = {};

			// Function COUNT_UNIQUE works on classifiers, used to count unique values of given classifiers
			_.each(expr.match(/COUNT_UNIQUE\(.+?\)/g), function (s) {
				scope["__countUniq__"] = data[colIdx];
				expr = expr.replace(s, "__countUniq__");
			});
			// write variable assignment for each total metric used in expression
			_.each(expr.match(/TOTAL\(.+?\)/g), function (s) {
				scope["__totalCount__"] = this._totalCount[colIdx];
				expr = expr.replace(s, "__totalCount__");
			}, this);

			var totalMetricsNum = this.totalMetricsNum;
			_.each(widget._getExpressionMetrics(expr), function (id) {
				const val = data[getMetricIdx(id, colMetrics, colIdx, totalMetricsNum)];
				scope[id] = val;
			}, this);

			var res = evaluate(expr, scope);
			return res;
		},

		/**
		 * Calculate dynamic metrics for current depth. Metrics to be taken from next level
		 * @param {TreeData} treeData - described in module description.
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {Number} countAdded - number of added metrics (for dynamic calculations)
		 * @param {number} level - our current level inside treeData, 1 == bottom
		 */
		parseDynamics: function (treeData, colMetrics, countAdded, level) {
			_.each(treeData, function (row, colIdx, obj) {
				if (_.isNumber(row)) {
					var metric = colMetrics[colIdx];
					if (metric.dynamic && !metric.custom) {
						if ((level == 0) || (getDynamicAggrFunc(metric) == AGGREGATION_FUNCTION)) {
							obj[colIdx] = this.calcMetric(metric.func, obj, colMetrics, colIdx, countAdded, metric.custom);
						} else {
							obj[colIdx] = undefined; // for metrics with non AGGREGATION_FUNCTION aggregation totals (level > 0) will be calculated later
						}
					}
				} else {
					this.parseDynamics(row, colMetrics, countAdded, level - 1);
				}
			}, this);
		},

		/**
		 * Analyze colTree and create map of horizontal subtotal and total fields -
		 * for each field it specify what field should be used for horizontal total accumulation
		 * @param {ColTree} colTree
		 * @param {Number} level
		 * @param {Number} totalLevel - level of total column. 0 if not total. colDepth for grandTotal.
		 * @return {Number} - current value group, "static" through recurring calls
		 */
		createHorizontalMap: function (colTree, level, horizontalMap, valGroup, totalLevel) {
			_.each(colTree, function (col, colId) {
				if (level == 2) {
					valGroup++;
				}
				if (level > 0) {
					if ((totalLevel == 0) && isTotalColId(colId)) {
						totalLevel = level;
					}
					valGroup = this.createHorizontalMap(col, level - 1, horizontalMap, valGroup, totalLevel);
				} else {
					if (totalLevel) {
						horizontalMap[col.field] = MIX_COL_SUF + TOTAL_COL_SUF + totalLevel + col.metric;
					} else {
						horizontalMap[col.field] = MIX_COL_SUF + valGroup + col.metric;
					}
				}
			}, this);
			return valGroup;
		},

		/**
		 * Calculate totals for "%mix" custom metrics, which refer to dynamic metric and %mix with row totals
		 * @param {TreeData} treeData - described in module description
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {Number} countAdded - number of added metrics (for dynamic calculations)
		 * @param {number} treeDepth - tree depth
		 * @param {ColTree} colTree
		 * @param {Number} horizontalMap
		 * @param {TreeData} [parentData] - pointer to the parent row inside treeData
		 */
		parseCustomTotals: function (treeData, colMetrics, countAdded, treeDepth, colTree, horizontalMap, parentData) {
			// first parse deeper levels (if any)
			for (var idx in treeData) {
				if (isVar(treeData[idx]) && !_.isNumber(treeData[idx])) {
					this.parseCustomTotals(treeData[idx], colMetrics, countAdded, treeDepth - 1, colTree, horizontalMap, treeData);
				}
			}
			// now parse current level, updating parent level values
			var store = this._config.metricStore;
			var totalMetricsNum = this.totalMetricsNum;
			var metric = null;
			for (var colIdx in treeData) {
				if (_.isNumber(treeData[colIdx])) {
					metric = colMetrics[colIdx];
					if (metric.custom && metric.total && store.get(metric.total).dynamic) { // Check that this is custom with "%mix" and it refers to dynamic
						if (parentData) {
							if (!treeDepth) { // bottom level
								treeData[colIdx] = treeData[getMetricIdx(metric.total, colMetrics, colIdx, totalMetricsNum)];
							}
							var parentVal = parentData[colIdx];
							parentData[colIdx] = _.isFinite(parentVal) ? parentVal + treeData[colIdx] : treeData[colIdx];
						} else {
							this._totalCount[colIdx] = treeData[colIdx];
						}
					}
					if ((metric.horizontalMix == MIX_ROW_GRANDTOTAL) || (metric.horizontalMix == MIX_ROW_TOTALGRAND)) {
						if (!isTotalColumn(colIdx, colTree)) {  // Exclude total columns values from horizontal aggregation
							var mixGrandTotalId = MIX_COL_SUF + metric.id;
							var mixGrandTotal = treeData[mixGrandTotalId] || 0;
							this._totalCount[mixGrandTotalId] = treeData[mixGrandTotalId] = mixGrandTotal + treeData[colIdx];
						}
					}
					if (metric.horizontalMix == MIX_ROW_SUBTOTAL) {
						var mixSubTotalId = horizontalMap[colIdx];
						var mixSubTotal = treeData[mixSubTotalId] || 0;
						this._totalCount[mixSubTotalId] = treeData[mixSubTotalId] = mixSubTotal + treeData[colIdx];
					}
				}
			}
		},

		/**
		 * Calculate value of dynamic metric
		 * @param {MetricObject} presentation - metric's presentation
		 * @param {array} data - line of cube data
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {number} colIdx - index of current column
		 * @param {number} countAdded - number of added metrics (for dynamic calculations)
		 * @param {TreeData} parentData - pointer to parent level of treeData
		 * @param {number} level - level inside tree (level==0 for leaves)
		 * @param {Number} horizontalMap
		 */
		calcCustomMetric: function (metric, data, colMetrics, colIdx, countAdded, parentData, level, horizontalMap) {
			/**
			 * Summary: validate val and res and call func(val, res) otherwice just return res intact
			 * @param {any} val
			 * @param {any} res
			 * @param {function} func - function(val, res)
			 * @return {any}
			 */
			function getRes(val, res, func) {
				if (isVar(val)) {
					if (isVar(res)) {
						return func(val, res);
					} else {
						return val;
					}
				}
				return res;
			}
			var presentation = metric.presentation;
			var res = undefined;
			var totalMetricsNum = this.totalMetricsNum;
			switch (presentation.subview) {
				case OP_SUM:
					_.each(presentation.calcForNode, function (metric) {
						res = getRes(data[getMetricIdx(metric, colMetrics, colIdx, totalMetricsNum)], res, function (val, res) { return val + res; });
					}, this);
					return res;
				case OP_DIFFERENCE:
					var A = data[getMetricIdx(presentation.metricANode, colMetrics, colIdx, totalMetricsNum)];
					var B = data[getMetricIdx(presentation.metricBNode, colMetrics, colIdx, totalMetricsNum)];
					var bPercentage = presentation.showResValueNode == "pct";
					if (isVar(A) && isVar(B)) {
						if (bPercentage) {
							if (B == 0) {
								return undefined;
							} else {
								return (A - B) / B;
							}
						} else {
							return A - B;
						}
					} else {
						return undefined;
					}
				case OP_PRODUCT:
					res = 1;
					_.each(presentation.calcForNode, function (metric) {
						res = getRes(data[getMetricIdx(metric, colMetrics, colIdx, totalMetricsNum)], res, function (val, res) { return val * res; });
					}, this);
					return res;
				case OP_AVERAGE:
					_.each(presentation.calcForNode, function (metric) {
						res = getRes(data[getMetricIdx(metric, colMetrics, colIdx, totalMetricsNum)], res, function (val, res) { return val + res; });
					}, this);
					return res / presentation.calcForNode.length;
				case OP_MARGIN:
					var revenue = data[getMetricIdx(presentation.revenueMetricNode, colMetrics, colIdx, totalMetricsNum)];
					var cost = data[getMetricIdx(presentation.costMetricNode, colMetrics, colIdx, totalMetricsNum)];
					if (isVar(revenue) && cost) {
						return (revenue - cost) / revenue;
					} else {
						return undefined;
					}
				case OP_MARKUP:
					revenue = data[getMetricIdx(presentation.revenueMetricNode, colMetrics, colIdx, totalMetricsNum)];
					cost = data[getMetricIdx(presentation.costMetricNode, colMetrics, colIdx, totalMetricsNum)];
					if (isVar(revenue) && cost) {
						return (revenue - cost) / cost;
					} else {
						return undefined;
					}
				case OP_MIX:
					var totalIdx = getMetricIdx(presentation.calcForSingleNode, colMetrics, colIdx, totalMetricsNum);
					var argMetric = colMetrics[totalIdx];
					var value;
					if (level && argMetric.dynamic) { // mix has it's own aggregation for totals
						value = data[colIdx]; // this is total row and referred metric is dynamic, so take total value from this metric cell, it was calculated in parseCustomTotals()
					} else {
						value = data[totalIdx]; // this is values row, take value from referred metric cell
					}
					var type = presentation.typePercentOfNode;
					var total;
					if (type == MIX_ROW_GRANDTOTAL) {
						total = data[MIX_COL_SUF + metric.id];
					} else if (type == MIX_ROW_SUBTOTAL) {
						mixTotalId = horizontalMap[colIdx];
						if (mixTotalId) {
							total = data[mixTotalId];
						}
					} else if (type == MIX_ROW_TOTALGRAND) {
						total = this._totalCount[MIX_COL_SUF + metric.id];
					} else if ((type == "colgrand") || !parentData) { // !parentData means that we are calculating grand total
						total = this._totalCount[colIdx];
					} else if (type == "colsub") {
						total = parentData[colIdx];
					}
					return total ? value / total : undefined;
				default:
					console.error("calcCustomMetric: unknown presentation subview ", presentation.subview);
			}
			return data[colIdx];
		},

		/**
		 * Calculate custom metrics for current depth.s
		 * @param {TreeData} treeData - described in module description.
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {Number} countAdded - number of added metrics (for dynamic calculations)
		 * @param {number} level - our current level inside treeData, 1 == bottom
		 * @param {Number[]} horizontalMap
		 * @param {TreeData} parentData - pointer to parent level of treeData
		 */
		parseCustom: function (treeData, colMetrics, countAdded, level, horizontalMap, parentData) {
			// first parse deeper levels (if any)
			for (var idx in treeData) {
				if (isVar(treeData[idx]) && !_.isNumber(treeData[idx])) {
					this.parseCustom(treeData[idx], colMetrics, countAdded, level - 1, horizontalMap, treeData);
				}
			}
			_.each(treeData, function (row, colIdx, obj) {
				if (_.isNumber(row)) {
					var metric = colMetrics[colIdx];
					if (metric && metric.custom) {
						if ((level == 0) || (getDynamicAggrFunc(metric) == AGGREGATION_FUNCTION) || (metric.presentation.subview == OP_MIX)) {
							obj[colIdx] = this.calcCustomMetric(metric, obj, colMetrics, colIdx, countAdded, parentData, level, horizontalMap);
						} else {
							obj[colIdx] = undefined; // for metrics with non AGGREGATION_FUNCTION aggregation totals (level > 0) will be calculated later
						}
					}
				}
			}, this);
		},

		/**
		 * Calculate total for given cell, update parent total row
		 * @param {number} newVal
		 * @param {treeData} parentData - reference to treeData row
		 * @param {number} idx - array index for parentData
		 * @param {string} func - type of aggregation function
		 */
		calcDynamicValueTotal: function (newVal, parentData, idx, func) {
			var parentVal = parentData[idx];
			switch (func) {
				case AGGREGATION_SUM:
					parentData[idx] = isVar(parentVal) ? newVal + parentVal : newVal;
					break;
				case AGGREGATION_MINIMUM:
					parentData[idx] = isVar(parentVal) ? Math.min(newVal, parentVal) : newVal;
					break;
				case AGGREGATION_MAXIMUM:
					parentData[idx] = isVar(parentVal) ? Math.max(newVal, parentVal) : newVal;
					break;
				case AGGREGATION_AVERAGE:
					if (_.isNumber(newVal)) {
						newVal = { value: newVal, count: 1 };
					}
					if (isVar(parentVal)) {
						parentData[idx].value += newVal.value;
						parentData[idx].count += newVal.count;
					} else {
						parentData[idx] = { value: newVal.value, count: newVal.count };
					}
					break;
				default:
					console.error("calcDynamicValueTotal: unknown dynamic metric aggregation");
			}
		},

		/**
		 * Calculate total row for dynamic (including static) rows
		 * @param {TreeData} treeData - described in module description.
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {Number} countAdded - number of added metrics (for dynamic calculations)
		 * @param {TreeData} parentData - pointer to the parent row inside treeData
		 */
		parseDynamicRowTotals: function (treeData, colMetrics, countAdded, parentData) {
			// first parse deeper levels (if any)
			for (var idx in treeData) {
				if (isVar(treeData[idx]) && !_.isNumber(treeData[idx])) {
					this.parseDynamicRowTotals(treeData[idx], colMetrics, countAdded, treeData);
				}
			}
			// now parse current level, updating parent level values
			for (var idx in treeData) {
				var val = treeData[idx];
				if (val && (_.isNumber(val) || val.value)) { // !!val.value = val is accumulative object for average
					var metric = colMetrics[idx];
					var func = getDynamicAggrFunc(metric);
					if (parentData && func && (func != AGGREGATION_FUNCTION) && (metric.presentation.subview != OP_MIX)) {
						this.calcDynamicValueTotal(val, parentData, idx, func);
					}
					if (val.value) {
						treeData[idx] = val.value / val.count; //convert avg accumulative object to value
					}
				}
			}
		},

		/**
		 * Create and object to use for fields sorting
		 * @param {Object} spec
		 * @param {string} value
		 * @return {Object}
		 */
		createFieldSortObject: function (spec, value) {
			var lbl = value;
			var enumIdx = null;
			if (spec) {
				if (spec.type == xml.ATTR_ENUM) {
					enumIdx = enums.getEnumIndex(value);
				} else if (spec.type == xml.ATTR_REF) {
					lbl = this._storeGetName(this.store, value);
				} else {
					lbl = this._specFormatValue(spec, value);
				}
			}
			return { label: lbl, value: value, enumIdx: enumIdx };
		},

		/**
		 * Sort column alphabetically, Recurring function.
		 * @param {ColTree} colTree - pointer to the current row of compound columns, pointer inside colTree structure
		 * @param {string[]} colCls - array of column classifiers
		 * @param {number} lvl
		 */
		sortColumns: function (colTree, colCls, lvl) {
			var res = {};
			if ((this._config.metricsPos == lvl) ||
				((this._config.metricsPos >= colCls.length) && (lvl == colCls.length))) { // don't sort metrics
				res = colTree;
			} else {
				var spec = (this._config.metricsPos == lvl) ? null :
					(this._config.metricsPos < lvl) ? this._getSpec(colCls[lvl - 1]) : this._getSpec(colCls[lvl]);
				var fields = []; // Array of column names that we will sort
				_.each(colTree, function (obj, txt) {
					fields.push(this.createFieldSortObject(spec, txt)); // form array of {name,keys, enumIdx} objects, to sort it and use for treeData reordering
				}, this);
				// Manipulate/recreate colTree branch to save sorted order
				_.each(sortFields(fields), function (obj) {
					res[obj.value] = colTree[obj.value];
				});
			}
			// Go dipper to next level and sort it
			_.each(res, function (obj, prop) {
				if (!obj.metric) { // Did we reached the bottom?
					res[prop] = this.sortColumns(obj, colCls, lvl + 1);
				}
			}, this);
			return res;
		},

		/**
		 * Sort rows alphabetically, Recurring function.
		 * @param {TreeData} treeData - pointer to the current row within treeData
		 * @param {string[]} rowCls - array of row classifiers
		 * @param {number} lvl
		 * @return {TreeData} - function returns sorted treeData
		 */
		sortRows: function (treeData, rowCls, level) {
			if (level >= rowCls.length) { // reached bottom, don't need to go further
				return treeData;
			}
			var fields = [];
			for (var prop in treeData) { // iterate through current level of treeData
				// iterate to the lower level and assign sorted element, replacing old unsorted one in treeData structure
				treeData[prop] = this.sortRows(treeData[prop], rowCls, level + 1);
				// Obtain name for given member (treeData rather contains keys, not names)
				var spec = this._getSpec(rowCls[level]);
				fields.push(this.createFieldSortObject(spec, prop)); // form array of {name,keys, enumIdx} objects, to sort it and use for treeData reordering
			}
			// Reorder treeData according to the fieldsSorted order
			var res = {};
			_.each(sortFields(fields), function (field) {
				_.some(treeData, function (obj, key) {
					if (field.value == key) {
						res[key] = obj;
						return true;
					}
				});
			});
			return res;
		},

		/**
		 * Add compare columns. Don't fill the data yet. Recurring function.
		 *
		 * Each compare column must have a reference to the column that we compare and reference to the column to be compared against.
		 * This function will find those columns, create new column for compare values and store required references i newly created column.
		 *
		 * We are iterating through columns and checking with configuration, if this column should have comparison.
		 * if it should, we create compare column
		 * newCol{
		 * 		field, 		// field of the column to  be compare
		 * 		cmpFiled, 	// field of the column to be compared agains
		 * 		metric		// metric key
		 * }
		 * and add this column to resulting object res{}. Res will then replace colTree, it is full clone of colTree plus it has compare columns.
		 *
		 * So, what are those cmpVs and cmpVsThis? Each classifier might have several values, and, if this classifier is chosen for comparison,
		 * we will compare metrics of each classifier's value against previous classifier value metrics. While we traversing along the colTree,
		 * on each level of colTree we deal with particular classifier. We parse it and create cmpVsThis object to hold column ids for every metric
		 * of this classifier. Lately, when we navigate to next value of this classifier, we store cmpVsThis to cmpVs and do following:
		 *  1 we will use cmpVs data to create compare columns
		 *  2 we gather next classifier metrics column ids to cmpVsThis, and so on, and so on. Recurring.
		 *
		 * @param {Object} colTree - columns structure object
		 * @param {string[]} colNames - array of column names, where column's property 'field' is index in this array
		 * @param {number} colCmpLvl - the level of classifier to compare against
		 * @param {number} lvl - current level of recursion aka level inside colTree
		 * @param {string} cmpVsName - name of the field to be compared against
		 * @param {Array} compareColumns - array of columns to be compared (output)
		 * @return {Object} - modified colTree branch
		 */
		addCompareColumns: function (colTree, colNames, colCmpLvl, lvl, cmpVsName, compareColumns) {
			var res = {}; // new version of colTree that have all required compare columns. It will replace old colTree.
			if (lvl == colCmpLvl) {
				if (!isVar(this._config.compareTo.val)) {
					cmpVs = cmpVsThis; // cmpVs is module variable 9global for this module, not really global)
				}
				cmpVsThis = {}; // Module variable: Object to hold column to compare against.
			}
			if (!colTree.metric) { // did we reached bottom level?
				_.each(colTree, function (colData, prop, obj) { // not a bottom level, continue iterating
					if (lvl == (colCmpLvl - 1)) {
						cmpVsName = prop;
					}
					var newCol = this.addCompareColumns(colData, colNames, colCmpLvl, lvl + 1, cmpVsName, compareColumns);
					if (!newCol) { // no column to compare with, just preserve column data
						res[prop] = colData;
					} else if (newCol && isVar(newCol.cmpVsField)) { // we have column to compare, preserve column and add new column for compare result
						newCol.field = colData.field + this.COMPARE_COL_SUF; // column with compare result have "field" property of original column plus COMPARE_COL_SUF
						newCol.cmpField = colData.field; // "field" of original column
						newCol.metric = colData.metric; // column with compare result have same metric as original column
						res[prop] = colData;
						res[newCol.field + this.delimiter(true) + newCol.colVsName] = newCol;
						compareColumns.push({ field: colData.field, cmpVsField: newCol.cmpVsField, compField: newCol.field }); // compare column pairs to be parsed and filled with data
					} else {
						res[prop] = newCol; // this is branch, not a leaf, save it
					}
				}, this);
				return res;
			} else { // bottom level, create column and remember it in cmpVsThis object for later use. Return compare column if this column is not right most.
				colName = colNames[colTree.field];
				var split = colName.split(this.DELIMITER_CHAR);
				var name = '';
				for (var i = colCmpLvl; i < split.length; i++) {
					name += this.delimiter(name) + split[i];
				}
				cmpVsThis[name] = { field: colTree.field, name: cmpVsName, metric: colTree.metric }; // save new column for later use
				// do we have column to compare against? (or it is right most columns)
				if (cmpVs[name] && (cmpVs[name].metric == colTree.metric) && (cmpVs[name].field != colTree.field)) {
					return { cmpVsField: cmpVs[name].field, colVsName: cmpVs[name].name }; // column with compare result has property "cmpVs" which contains "filed" property for the column to compare against
				} else {
					return null;
				}
			}
		},

		/**
		 * For Compare Columns calculate difference. Just subtract one from another.
		 * @param {TreeData} treeData - entry in treeData to parse. See detailed description on the top of this module.
		 * @param {Array} compareColumns - array of columns to be compared
		 */
		parseCompare: function (treeData, compareColumns) {
			const bPercent = this._config.compareTo.bPercent;
			_.each(compareColumns, function (compColumn) {
				const { field, cmpVsField, compField } = compColumn;
				if (isVar(treeData[field]) && isVar(treeData[cmpVsField])) {
					const newVal = treeData[field];
					const oldVal = treeData[cmpVsField];
					const diff = newVal - oldVal;
					treeData[compColumn.compField] = bPercent ? diff / oldVal : diff;
				}
			}, this);
			_.each(treeData, function (_colData) {
				if (!_.isNumber(_colData)) {
					this.parseCompare(_colData, compareColumns);
				}
			}, this);
		},

		/**
		 * Find column to compare to. Recurring function.
		 * @param {Object} colTree - columns structure object
		 * @param {string[]} colNames - array of column names, where column's property 'field' is index in this array
		 * @param {number} colCmpLvl - the level of classifier to compare against
		 * @param {number} lvl - current level of recursion aka level inside colTree
		 * @param {string} cmpVsName - name of the field to be compared against
		 */
		findCompareToVal: function (colTree, colNames, colCmpLvl, lvl, cmpVsName) {
			var stopParsing = false;
			if ((lvl == colCmpLvl) && (!cmpVsName)) {
				cmpVs = cmpVsThis;
				cmpVsThis = {};
			}
			if (!colTree.metric) {
				_.some(colTree, function (colData, prop, obj) {
					if (lvl == (colCmpLvl - 1)) {
						if (prop == this._config.compareTo.val) { // we found desired classifier, but we are no at bottom of compound column
							cmpVsName = prop;
							stopParsing = true;
						} else {
							return false;
						}
					}
					// Proceed to the lower levels of compound columns until we reach metrics.
					// Call recurring function but preserve stopParsing == true if desired classifier was already found.
					return stopParsing = this.findCompareToVal(colData, colNames, colCmpLvl, lvl + 1, cmpVsName) || stopParsing;
				}, this);
			} else { // Once we reach metrics, remember all column ids under desired classifier and use it for compare against
				colName = colNames[colTree.field];
				var split = colName.split(this.DELIMITER_CHAR);
				var name = '';
				for (var i = colCmpLvl; i < split.length; i++) {
					name += this.delimiter(name) + split[i];
				}
				cmpVsThis[name] = { field: colTree.field, name: cmpVsName, metric: colTree.metric };
			}
			return stopParsing;
		},

		/**
		 * Collect first classifier's values (used for column compare dialog)
			 * @param {Object} colTree - columns structure object
		 * @param {string[]} colCls - array of column classifiers keys
		 * @param {Number} metricsPos - position of metrics
		 * @param {Number} firstClsPos - position of first classifier
		 * @param {Number} lvl - current level inside colTree structure
		 * @return {Object[]} - {key, name}[] of values
		 */
		_getFirstColClsValues: function (colTree, colCls, metricsPos, firstClsPos, lvl, values) {
			if (colCls.length == 0) {
				return [];
			}
			// if metrics are on top and above first classifier, we have to iterate through each metric and collect all first classifier values
			// As values might repeat, we use hashing array to exclude duplicates
			if (lvl == firstClsPos) {
				var spec = (metricsPos < lvl) ? this._getSpec(colCls[lvl - 1]) : this._getSpec(colCls[lvl]);
				for (var key in colTree) {
					if (key.indexOf(this.DELIMITER_CHAR) >= 0) { // Skip special columns like compare and total columns
						continue;
					}
					var name = this._specFormatValue(spec, key);
					if (spec.type == xml.ATTR_REF) {
						name = this._storeGetName(this.store, name);
					}
					if (name == '') {
						name = locale.getEmptyName();
					}
					values[key] = name;
				}
			} else {
				_.each(colTree, function (colData) {
					this._getFirstColClsValues(colData, colCls, metricsPos, firstClsPos, lvl + 1, values);
				}, this);
			}
			var res = [];
			if (lvl == 0) {// turn hashing array to the store data format {id, name}[]
				for (var prop in values) {
					res.push({ id: prop, name: values[prop] });
				}
			}
			return res;
		},

		/**
		 * Collect column classifiers keys and names
		 * @param {string[]} colCls - array of column classifiers keys
		 * @return {Object[]} - {key, name}[] of values
		 */
		_getColClsNames: function (colCls) {
			var names = [];
			var store = this._config.classifierStore;
			for (var i = 0; i < colCls.length; i++) {
				names.push({ id: colCls[i], name: store.query({ id: colCls[i] })[0].name || colCls[i] });
			}
			return names;
		},

		/**
		 * For each classifier with enabled total column create columns for each metric
		 * @param {string} path - path inside column data object
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {Object} totalColFields - column ids for total columns
		 * @param {string[]} metrics - config.metrics. Inside parse cube procedure it also have added virtual metrics
		 */
		addTotalColumnsFields: function (path, colMetrics, totalColFields, metrics) {
			var metricStore = this._slicerConfig.widget.metricStore;
			totalColFields[path] = {};
			for (var j = 0; j < metrics.length; j++) {
				totalColFields[path][metrics[j]] = colMetrics.length;
				var metric = metricStore.get(metrics[j]);
				colMetrics.push(metric);
			}
		},

		/**
		 * Add Total columns. Don't fill the data yet. Recurring function.
		 *
		 * In order to create total columns, we traversing colTree and see, if the classifier we are on has totals enabled.
		 * Then we create total column, gather the data that is required to calculate total columns and assign this data to
		 * newly created total column. We don't fill created total column with data in this function.
		 *
		 * As we found that we need total column, we have to scan the entire row and collect all values for each metrics.
		 * So, we are scanning the row, and for each classifier value, and for every metric we collect arrays of columnId
		 * that should be used with total calculation. We put result in res[] array which have form of two dimensional array
		 * [
		 *	[fieldId, fieldId...] // column ids from which I should take data for first metric
		 *	[fieldId, fieldId...] // column ids from which I should take data for second metric
		 *	..
		 * ]
		 * So lately, we will take this array, and use treeData to gather all values and calculate totals in function parseTotalColumnsStatic()
		 *
		 * We should keep in mind, that we use compound columns, so for each classifier (except for ones on the very bottom)  we have to create
		 * separate column for each level of compound column and one main very bottom level column which will actually hold the data.
		 *
		 * @param {Object} colTree - columns structure object
		 * @param {number} lvl - current level of recursion aka level inside colTree
		 * @param {string[]} colCls - array of column classifiers keys
		 * @param {Object} totalColumns - {field, metric, values}
		 * @param {Object} totalColFields - column ids for total columns
		 * @param {MetricObject[]} colMetrics - array of metrics per column
		 * @param {string[]} metrics - config.metrics. Inside parse cube procedure it also have added virtual metrics
		 * @param {number} countAdded - number of virtual metrics added
		 * @param {string} path - path inside column data object
		 * @return {number[][]} - used for recurring calls only
		 */
		addTotalColumns: function (colTree, lvl, colCls, totalColumns, totalColFields, colMetrics, metrics, countAdded, path) {
			function getMetric(colData) {
				while (!colData.metric) { // first key will never be a total nor compare column, it will always be genuine column
					colData = colData[Object.keys(colData)[0]];
				}
				return colData.metric;
			};
			if (colTree.cmpField) {
				return [];
			}
			var cfg = this._config;
			var res = []; // see description for details on this array
			if (!colTree.metric) { // we are not at the very bottom, still traversing
				_.each(colTree, function (colData, idx, obj) {
					// ret[] is the same as res, but it only contains data about single column. It has number of members equal to number of
					// metrics, and each member is fieldId for corresponding metric's column for given classifier;
					var ret = this.addTotalColumns(colData, lvl + 1, colCls, totalColumns, totalColFields, colMetrics,
						metrics, countAdded, (lvl == cfg.metricsPos) ? path : path + this.delimiter(path) + idx);
					for (var i = 0; i < metrics.length; i++) {
						if (ret[i]) {
							if (!res[i]) {
								res[i] = ret[i].slice(0);
							} else {
								for (var j = 0; j < ret[i].length; j++) {
									if (res[i].indexOf(ret[i][j]) < 0) {
										res[i].push(ret[i][j]);
									}
								}
							}
						}
					}
				}, this);
				if (lvl != cfg.metricsPos) {
					var level = lvl - ((cfg.metricsPos < lvl) ? 1 : 0); // level - inside colCls; lvl - inside Compound Columns
					var classifier = colCls[level];
					if (cfg.showTotals[classifier]) {
						// When we will render total column, we need somehow distinguish total columns from normal columns,
						// We introduce following format for total columns (similar to compare columns)
						// Column id will have a form of
						// for top most part of compare column (consider compound columns) we will use:
						// SUF+DELIMITER+DELIMITER+SUF where SUF = TOTAL_COL_SUF and DELIMITER is DELIMITER_CHAR
						// for intermediate level of compound column it will be SUF+DELIMITER
						// for bottom level it will be ColID+SUF+DELIMITER+MetricKey
						var first = this.DELIMITER_CHAR + this.TOTAL_COL_SUF; // first part of forming colId
						var depth = colCls.length - lvl; // how many levels separate total top from bottom level
						var col = colTree;
						// now we need to insert one very top part and several intermediate part of the column
						for (var i = 0; i < depth; i++) {
							col = col[this.TOTAL_COL_SUF + this.DELIMITER_CHAR + first] = {};
							first = '';
						}
						var thePath = classifier + this.delimiter(path) + path; // Path to find our metrics colId inside totalColFieldsstructure
						if (!totalColFields[thePath]) { // if we did not added metrics rows yet -add ti nowm all metrics at once, so they will have consequent ids.
							this.addTotalColumnsFields(thePath, colMetrics, totalColFields, metrics);
						}
						// Now create total column/metric column
						if (cfg.metricsPos > lvl) { // metrics below current classifier, just add column for every metric
							for (i = 0; i < metrics.length; i++) {
								var colId = totalColFields[thePath][metrics[i]];
								var c = {
									field: colId,
									metric: metrics[i],
									values: res[i]
								};
								totalColumns[colId] = c; // add column to total columns structure, to use with parseTotalColumnsStatic function
								var bRealMetric = i < (metrics.length - countAdded); // deal with virtual metrics
								if (bRealMetric) {
									col[colId + this.TOTAL_COL_SUF + this.DELIMITER_CHAR + this._resolveKeyName(metrics[i]) + first] = c;
								}
							}
						} else { // metrics above this classifier
							var m = getMetric(colTree);
							var colId = totalColFields[thePath][m];
							// Create column for this classifier within compound columns
							col[colId + this.TOTAL_COL_SUF + this.DELIMITER_CHAR + first] = {
								field: colId,
								metric: m,
								values: res[metrics.indexOf(m)]
							};
							// Create column for each metric
							var dst = colId;
							var dst0 = dst - dst % metrics.length; // Find colId of first src metric in this set
							for (var i = 0; i < metrics.length; i++) {
								// add column to total columns structure, to use with parseTotalColumnsStatic function
								// we only need bottom level columns here, we don't need parts of compound columns
								totalColumns[dst0 + i] = {
									field: dst0 + i,
									metric: metrics[i],
									values: res[i]
								};
							}
						}
					}
				}
			} else { // we reached the bottom of colData structure and now can pick up column field id and add it to res
				// each classifier always have full set of metrics (metrics). Some metrics are real and will be displayed,
				// another ones are only for calculations, they are "virtual" and will not be seen to the users.
				// we need to find the entire metrics set for total calculation
				if (!isVar(res[metrics.indexOf(colTree.metric)])) { // if this set of metrics is not yet found
					var src = colTree.field; // one of the source metrics
					if (!isNaN(src)) { // exclude compare columns, which have filed in the form of {number}{suffix} like "1c"
						var src0 = src - src % metrics.length; // Find colId of first src metric in this set
						for (var i = 0; i < metrics.length; i++) {
							res[i] = [src0 + i];
						}
					}
				}
			}
			return res;
		},

		/**
		 * In this function we are filing in Total Columns values for static metrics.
		 *
		 * No dynamic metrics, no sub totals yet, so we are only using bottom level of treeData.
		 * We use totalColumns structure that we created in addTotalColumns()
		 * totalColumns: {Object}
		 * {
		 * 	colId { // each total column has an entry like that. totalColumn will have one entry per each total column.
		 * 		filed: {numer} - column id, here it is pure number,
		 * 		metric: {string} - metric key
		 * 		values: {number[]} - array of column ids to gather data from
		 *  }
		 *  ...
		 * }
		 *
		 * We traversing through totalColumns and processing one column at a time.
		 * For each column we retrieve other columns data, and creating array of values. Later, common function parseTotal() will
		 * properly aggregate this data using appropriate metric function. We assigning value 0 for dynamic metrics.
		 * See calcTotalStepValue() function description for explanation "why zero" and more info about values aggregation.
		 *
		 * Fields with dynamic metrics for totalColumns will be calculated later with common function parseDynamic()
		 * "Common function" means that we don't need special calculation functions for totalColumns
		 *
		 * @param {Object} treeData - rows structure object
		 * @param {Object} totalColumns - {field, metric, values}
		 * @param {Memory} metricsStore - config.metrics. Inside parse cube procedure it also have added virtual metrics
		 * @param {number} treeDepth - tree column depth
		 * @param {number} lvl - current lvl inside treeData
		 */
		parseTotalColumnsStatic: function (treeData, totalColumns, metricStore, treeDepth, lvl) {
			if (lvl == treeDepth) { // Did we reached bottom level yet?
				_.each(totalColumns, function (column, field) {
					var metric = metricStore.get(column.metric);
					if (metric.dynamic && !metric.total && !metric.uniq) {
						treeData[field] = 0;
					} else {
						var values = column.values; // array of column ids that we should collect data from
						for (var i = 0; i < values.length; i++) {
							var val = treeData[values[i]]; // retrieve one column data from treeData
							if (val) { // some grid cells are undefined
								for (var j = 0; j < val.length; j++) {
									if (!treeData[field]) {
										treeData[field] = [];
									}
									if (metric.uniq) {
										if (treeData[field].indexOf(val[j]) < 0) { // for COUNT_UNIQ metrics create array of uniq values
											treeData[field].push(val[j]);
										}
									} else {
										treeData[field].push(val[j]); // create array of values for all other types of metrics
									}
								}
							}
						}
					}
				}, this);
			} else {
				// iterating through treeData until we reach bottom level.
				_.each(treeData, function (data) {
					this.parseTotalColumnsStatic(data, totalColumns, metricStore, treeDepth, lvl + 1);
				}, this);
			}
		},

		/**
		 * Parse cube data, generate nested objects data structure according to classifiers configuration.
		 * Every time configuration changes, nested objects data must be re-generated and grid re-initialized
		 * Nested objects example: row classifier->row classifier 2->column classifier -> list of metrics
		 * @param {string[]} metrics
		 * @param {string[]} rowCls - row classifiers.
		 * @param {string[]} colCls - column classifiers.
		 * @return {Object} colTree - nested object, contains all columns we will need.
		 */
		parseCube: function (metrics, rowCls, colCls) {
			function pushValues() {
				var val;
				colMetrics[colIdx] = metric;
				if (metric.dynamic) {
					haveDynamic = true; // flag indicating that dynamic function present and should be parsed (special parsing pass)
					val = 0;
					if (metric.total) {
						val = data[i][store.getKeyIndex(metric.total)];
					} else if (metric.uniq) {
						val = data[i][store.getKeyIndex(metric.uniq)];
					}
				} else {
					val = data[i][store.getKeyIndex(metrics[j])];
				}

				// Client side aggregation. Creating array of values for calculations with metric.func.
				// This is required becuase of function COUNT_UNIQ, which may cause server request to include classifier, which is not visible in the grid.
				if (!obj[colIdx]) {
					obj[colIdx] = [];
				}
				if (metric.func == FUNC_COUNT && metric.id != ExtractValues.METRIC_COUNT) {
					//don't delete yet:	obj[colIdx].push(1); // function COUNT on arbitrary metric (not on _Count__) will count number of occurrences in Cube. In Totals it will be aggregated as SUM.
					obj[colIdx] = [1]; // function COUNT will count number of lines in grid
				} else if (metric.uniq) {
					if (obj[colIdx].indexOf(val) < 0) { //only push unique value
						obj[colIdx].push(val);
					}
				} else {
					obj[colIdx].push(val);
				}
			}

			var colNames = [];
			var colTree = {};
			var treeData = {};
			var haveDynamic = false;
			var haveCustom = false;
			var haveHorizontalMix = false;
			var store = this.store;
			var data = store._data;
			this.dataDate = store.bReportDB ? "" : store._dataDate;
			var metricsPos = this._config.metricsPos;
			var widget = this._slicerConfig.widget;
			var metricStore = widget.metricStore;
			var colIdx = 0;
			var colMetrics = [];
			var countAdded = 0;
			this.bEmpty = false;

			this._totalCount.length = 0;
			this._columnCount = 0;

			// if there are no rows or no metrics or one of stores is missing - slicer will be empty, no calculations require
			if ((rowCls.length) && (metrics.length) && store && metricStore) {

				// determine if we need extra metrics for dynamic calculations. These are "Virtual" metrics, they should not be displayed
				var mkeys = [];
				var haveWeight = false;
				var haveWeightedAverage = false;
				_.each(metrics, function (mkey) {
					var metric = metricStore.get(mkey);
					haveCustom = haveCustom || metric.custom;
					haveHorizontalMix = haveHorizontalMix || (!!metric.horizontalMix);
					if (metric) {
						haveWeight = haveWeight || metric.weight;
						const weightAttributeName = metric.weightAttributeName;
						if (weightAttributeName) {
							// static metric with Weighted Average aggregation require Weight attribute for calculation
							mkeys = _.union(mkeys, [weightAttributeName]);
							haveWeightedAverage = true;
						} else if (metric.dynamic) {
							// dynamic metrics might require other metrics for calculataion
							mkeys = _.union(mkeys, widget.getInvolvedMetrics(metric, true));
						}
					}

				});
				this.bWeightedAverageProcessing = haveWeight && haveWeightedAverage;

				var diff = _.difference(mkeys, metrics);
				countAdded = diff.length;
				metrics = metrics.concat(diff);
				this.totalMetricsNum = this._config.metrics.length + countAdded;

				// metrics with Weighted Average aggregation need to have reference to weighted metric
				// calculate now and just once rather then calculate later on each aggregation step
				// this calculation must be done after metrics = metrics.concat(diff);
				_.each(metrics, function (mkey) {
					var metric = metricStore.get(mkey);
					if (metric && metric.weightAttributeName) {
						metric.weightAttributeIdx = metrics.indexOf(metric.weightAttributeName);
					}
				});

				try {
					// validate metrics and classifiers. Make sure they exists and resolveable
					_.each(metrics.concat(rowCls).concat(colCls), function (mkey) {
						if (!widget.getSpec(mkey).ren) {
							var metric = metricStore.get(mkey);
							const errMessage = locale.resource("SLICER_ATTRIBUTE_NOT_FOUND", [metric.name]);
							console.error(errMessage);
							widget.set("contentMessage", errMessage);
							throw new Error(errMessage);
						}
					});
					// check if slicer data is available. No need for message - detailed messge produced by request error handler
					if (!data) {
						const errMessage = locale.resource("SLICER_GENERAL_ERROR");
						console.error("NO CUBE DATA");
						widget.set("contentMessage", errMessage);
						throw new Error(errMessage);
					}
					console.time("Parse Cube");
					for (var i = 0; i < data.length; i++) {
						var obj = treeData;

						// create a path to metrics through row classifiers nested objects, create required nested objects
						for (var j = 0; j < rowCls.length; j++) {
							obj = obj[data[i][store.getKeyIndex(rowCls[j])]] || (obj[data[i][store.getKeyIndex(rowCls[j])]] = {});
						}

						// continue path through column classifiers
						var colName = ""; // create a name to use with dgrid column "field"
						var colObj = colTree; // colObj will point inside colTree, but we will actually continue modifying colTree;
						for (j = 0; j < metricsPos && j < colCls.length; j++) {
							colName += this.delimiter(colName) + data[i][store.getKeyIndex(colCls[j])]; // col name
							colObj = colObj[data[i][store.getKeyIndex(colCls[j])]] || (colObj[data[i][store.getKeyIndex(colCls[j])]] = {}); // object contains metrics
						}

						if (metricsPos >= colCls.length) {
							// If metrics are located at the end of column classifiers, create object with metrics
							// obj->Object->Object...->List of column name:metrics pairs, pair is ready for dgrid
							for (j = 0; j < metrics.length; j++) {
								var currColName = colName + this.delimiter(colName) + metrics[j];
								var metric = metricStore.get(metrics[j]);
								var bRealMetric = j < (metrics.length - countAdded); // "not real" metrics should not be added to colObj[], but to treeData[] (aka obj)
								if (metric) {
									colIdx = colNames.indexOf(currColName);
									if (colIdx < 0) {
										colIdx = colNames.push(currColName) - 1;
										if (bRealMetric) {
											this._columnCount++;
											colObj[metrics[j]] = {
												field: colIdx,
												metric: metrics[j]
											};
										}
									}
									pushValues();
								}
							}
						} else {
							// if metrics are not at the end, create child column for every metrics, and parse through the rest of classifiers for every metric
							// then add metric value at the end of the tree.
							for (j = 0; j < metrics.length; j++) {
								var lastCol = '';
								var currColName = colName + this.delimiter(colName) + metrics[j];
								var bRealMetric = j < (metrics.length - countAdded);
								if (bRealMetric) {
									var _colObj = colObj[metrics[j]] || (colObj[metrics[j]] = {});
								}
								var metric = metricStore.get(metrics[j]);
								if (metric) {
									// parse through the rest of classifiers
									for (var k = metricsPos; k < colCls.length; k++) {
										currColName += this.delimiter(currColName) + data[i][store.getKeyIndex(colCls[k])]; // col name
										lastCol = data[i][store.getKeyIndex(colCls[k])];
										if ((k < colCls.length - 1) && bRealMetric) {
											_colObj = _colObj[lastCol] || (_colObj[lastCol] = {}); // object contains metrics
										}
									}
									// add metric value at the end of every tree branch
									colIdx = colNames.indexOf(currColName);
									if (colIdx < 0) {
										colIdx = colNames.push(currColName) - 1;
										if (bRealMetric) {
											this._columnCount++;
											_colObj[lastCol] = {
												field: colIdx,
												metric: metrics[j]
											};
										}
									}
									pushValues();
								}
							}
						}
					}
					console.timeEnd("Parse Cube");

					console.time("Sort Columns");
					colTree = this.sortColumns(colTree, colCls, 0);
					console.timeEnd("Sort Columns");

					console.time("Sort Rows");
					treeData = this.sortRows(treeData, rowCls, 0);
					console.timeEnd("Sort Rows");



					// flag indicating that we have to create compare columns and fill them, it is also the level of compare classifier position in colTree object
					var cmp = this._config.compareTo;
					var compareColumns = []; // compare column pairs to be parsed and filled with data {cmpField:{number}+COMPARE_COL_SUF, cmpVsField:{number})
					if (cmp && cmp.key && cmp.show) {
						var bValidSearch = true;
						var colCmpLvl = colCls.indexOf(cmp.key);
						if ((colCmpLvl >= 0) && (metricsPos <= colCmpLvl)) {
							colCmpLvl++;
						}
						if (colCmpLvl >= 0) {
							cmpVs = {};
							cmpVsThis = {};
							console.time("Create Compare Columns");
							if (isVar(cmp.val)) {
								// if option "compare to specific" enabled, we need to find this "specific" column.
								// we will dive to colTree to specific level, level corresponding to selected classifier and then will create and remember cmpVs
								// and use this cmpVs for all compare column, instead of create new cmpVs for every classifier value
								// see function addCompareColumns() description for details.
								this.findCompareToVal(colTree, colNames, colCmpLvl + 1, 0);
								cmpVs = cmpVsThis;
								bValidSearch = !_.isEmpty(cmpVsThis);
							}
							if (bValidSearch) {
								colTree = this.addCompareColumns(colTree, colNames, colCmpLvl + 1, 0, '', compareColumns); // colCmpLvl+1because we have to dive below this level
							} else {
								console.info('Slicer Compare Column: Specific column for comparison not found'); // not an exception, but we will need to be able to see this in console
							}
							console.timeEnd("Create Compare Columns");
						}
					}

					var treeDepth = rowCls.length;
					var colDepth = colCls.length;

					console.time("Create Total Columns");
					colIdx = colNames.length;
					var totalColFields = {};
					var totalColumns = {};
					this.addTotalColumns(colTree, 0, colCls, totalColumns, totalColFields, colMetrics, metrics, countAdded, '');
					if (!_.isEmpty(totalColumns)) {
						this.parseTotalColumnsStatic(treeData, totalColumns, metricStore, treeDepth, 0);
						haveDynamic = true;
					}
					console.timeEnd("Create Total Columns");

					console.time("Parse Row Totals");
					for (var i = 0; i < treeDepth; i++) {
						this.parseTotals(treeData, colMetrics, treeDepth, i, i == (treeDepth - 1));
					}
					console.timeEnd("Parse Row Totals");

					if (haveDynamic) {
						console.time("Parse Dynamic");
						this.parseDynamics(treeData, colMetrics, countAdded, treeDepth);
						console.timeEnd("Parse Dynamic");
					}

					if (haveCustom) {
						console.time("Parse Custom");
						var horizontalMap = [];
						if (haveHorizontalMix) {
							this.createHorizontalMap(colTree, colDepth, horizontalMap, 0, 0);
						}
						this.parseCustomTotals(treeData, colMetrics, countAdded, treeDepth, colTree, horizontalMap);
						this.parseCustom(treeData, colMetrics, countAdded, treeDepth, horizontalMap);
						console.timeEnd("Parse Custom");
					}

					if (haveDynamic) {
						console.time("Parse Dynamic Row Totals");
						this.parseDynamicRowTotals(treeData, colMetrics, countAdded);
						console.timeEnd("Parse Dynamic Row Totals");
					}

					if (compareColumns.length) {
						console.time("Parse Compare");
						this.parseCompare(treeData, compareColumns);
						console.timeEnd("Parse Compare");
					}
					var firstClsPos = (metricsPos == 0) ? 1 : 0;
					this.firstColClsValuesStore = new Memory({ 'data': this._getFirstColClsValues(colTree, colCls, metricsPos, firstClsPos, 0, {}) });
					this.colClsNamesStore = new Memory({ 'data': this._getColClsNames(colCls) });

					this.addDummyColumn(colTree, colCls.length + 1); // depth of compound columns equal to number of columnClassifiers + 1 (metrics)

				} catch (ex) {
					console.error("Parse Cube failed. ex=", ex);
				}
			} else { // we have no Row Classifiers
				this.firstColClsValuesStore = new Memory({ 'data': [] });
				this.colClsNamesStore = new Memory({ 'data': [] });
				// if we dont have errors yet, create "SLICER_CONFIG_EMPTY" error
				if (!widget.contentMessage) {
					const errMessage = locale.resource("SLICER_CONFIG_EMPTY");
					console.error(errMessage);
					widget.set("contentMessage", errMessage);
					this.bEmpty = true;
				}
			}
			// Slicer Analyze: Collect Slicer dimensions info  --------------
			this.numBranch = -1; // -1 because we subtract root branch
			this.numLeaves = 0;
			var numCol = this._columnCount;
			this.countRows(treeData);
			console.info("This Slicer has - Columns:", numCol, "; Rows", this.numBranch + this.numLeaves, "; Branches:",
				this.numBranch, " Leaves:", this.numLeaves, "Cells:", numCol * (this.numBranch + this.numLeaves),
				"; Invisible Metrics added:", countAdded);
			// --------------end Slicer Analyze

			this._totalCount.length = 0;

			return { colTree: colTree, treeData: treeData };
		},

		/**
		 * Slicer Analyze: Collect Slicer dimensions info - count number of rows
		 * @param {Object} obj - treeData
		 */
		countRows: function (obj) {
			var numNumbers = 0;
			var numObjects = 0;
			var hasObject = false;
			_.each(obj, function (o) {
				if (_.isNumber(o)) {
				} else {
					hasObject = true;
					this.countRows(o);
				}
			}, this);
			if (hasObject) {
				this.numBranch++;
			} else {
				this.numLeaves++;
			}
			return;
		},

		/**
		 * Creates Dummy Column which works as place holder in the case when there are unused space after very right column
		 * @param {Object} colTree - columns structure object
		 * @param {number} compoundHeaderDepth - depth of compound columns header
		 */
		addDummyColumn: function (colTree, compoundHeaderDepth) {
			var obj = colTree;
			for (var i = 0; i < compoundHeaderDepth; i++) {
				obj = obj['dummy' + this.DELIMITER_CHAR] = { isDummy: true };
			}
		}

	});
});

